import { StyleSheet, css } from 'aphrodite';
import { FC, useState } from 'react';

import { Button } from '../../../web/components-v2/Button';
import { AIMarkdownResponse } from '../../../web/components-v2/ai/AiMarkdownResponse';
import { THEME } from '../../../web/components-v2/Theme/theme';
import type { CoachMessage } from '../ai';
import { IconPin, IconPinFill, IconSparkles } from './icons';

/**
 * Props for {@link MessageBlock}. Renders a single message in the thread; the
 * visual shape is driven by `msg.role`:
 *
 *  - `assistant`  → left-aligned block with a Sales Coach avatar + timestamp,
 *    pin toggle on hover (always visible when pinned).
 *  - `user`       → right-aligned bubble, tight bottom-right corner,
 *    timestamp revealed below-right on hover.
 *  - `transcript` → labeled mini-bubble with a speaker prefix ("Lead:" / "You:"),
 *    timestamp, and a side accent that differentiates rep vs lead utterances.
 */
export type MessageBlockProps = {
	msg: CoachMessage;
	isPinned?: boolean;
	/** Omit to render assistant replies without the pin toggle. */
	onTogglePin?: () => void;
};

export const MessageBlock: FC<MessageBlockProps> = ({ msg, isPinned = false, onTogglePin }) => {
	if (msg.role === 'transcript') {
		return <TranscriptLine text={msg.text} time={msg.time} />;
	}

	if (msg.role === 'user') {
		return <UserBubble msg={msg} />;
	}

	return <AssistantBlock msg={msg} isPinned={isPinned} onTogglePin={onTogglePin} />;
};

const AssistantBlock: FC<{ msg: CoachMessage; isPinned: boolean; onTogglePin?: () => void }> = ({
	msg,
	isPinned,
	onTogglePin,
}) => {
	const [hovered, setHovered] = useState(false);
	const pinColor = msg.pinColor || 'var(--accent)';
	const showToolbar = hovered || isPinned;

	return (
		<div className={css(styles.aiWrap)} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
			<div className={css(styles.aiHeader)}>
				<span className={css(styles.avatar)}>
					<IconSparkles size={11} sw={2} />
				</span>
				<span className={css(styles.aiAuthor)}>Sales Coach</span>
				<span className={css(styles.aiTime)}>{msg.time}</span>
			</div>
			<div className={css(styles.aiBody)}>
				<MessageText text={msg.text} />
			</div>

			{onTogglePin && (
				<div className={css(styles.toolbar, showToolbar ? styles.toolbarVisible : styles.toolbarHidden)}>
					<Button
						type='button'
						variant='ghost'
						size='icon'
						styles={[styles.pinBtn]}
						style={{ color: isPinned ? pinColor : undefined }}
						title={isPinned ? 'Unpin from rail' : 'Pin to rail'}
						onClick={onTogglePin}
						aria-pressed={isPinned}
					>
						{isPinned ? <IconPinFill size={14} color={pinColor} /> : <IconPin size={14} />}
					</Button>
				</div>
			)}
		</div>
	);
};

const UserBubble: FC<{ msg: CoachMessage }> = ({ msg }) => {
	const [hovered, setHovered] = useState(false);

	return (
		<div className={css(styles.userWrap)} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
			<div className={css(styles.userBubble)}>
				<MessageText text={msg.text} />
				<div className={css(styles.userTime, hovered ? styles.userTimeVisible : styles.userTimeHidden)}>{msg.time}</div>
			</div>
		</div>
	);
};

/**
 * Parse a transcript message's text into `{ speaker, body }`. Upstream the
 * text is formatted as `"<speaker>: <text>"` by `addTranscriptEntry`, where
 * `<speaker>` is one of the neutral buckets (`'lead' | 'rep' | 'other'`) or
 * a provider-supplied human label (e.g. `'John Doe'`). When the prefix is
 * missing (e.g. legacy persisted entries without a colon), the speaker is
 * unknown and the whole text is rendered as the body.
 */
export const parseTranscriptText = (text: string): { speaker: string | null; body: string } => {
	const colonIdx = text.indexOf(':');
	if (colonIdx <= 0) return { speaker: null, body: text };
	const speaker = text.slice(0, colonIdx).trim();
	const body = text.slice(colonIdx + 1).trim();
	if (!speaker || !body) return { speaker: null, body: text };
	// Guard: a transcript that legitimately starts with a long phrase before a
	// colon shouldn't be misread as a multi-sentence speaker label.
	if (speaker.length > 32) return { speaker: null, body: text };
	return { speaker, body };
};

/**
 * Categorize a parsed speaker into one of the visual buckets used to drive
 * label and accent color. The neutral buckets (`'lead' | 'rep'`) come from
 * `TranscriptSpeaker`; anything else is treated as a custom label.
 */
export const classifySpeaker = (speaker: string | null): { kind: 'lead' | 'rep' | 'other'; label: string } => {
	if (!speaker) return { kind: 'other', label: 'Transcript' };
	const lower = speaker.toLowerCase();
	if (lower === 'lead') return { kind: 'lead', label: 'Lead' };
	if (lower === 'rep') return { kind: 'rep', label: 'You' };
	if (lower === 'other') return { kind: 'other', label: 'Transcript' };
	return { kind: 'other', label: speaker };
};

const TranscriptLine: FC<{ text: string; time: string }> = ({ text, time }) => {
	const { speaker, body } = parseTranscriptText(text);
	const { kind, label } = classifySpeaker(speaker);
	const sideStyle =
		kind === 'lead' ? styles.transcriptLead : kind === 'rep' ? styles.transcriptRep : styles.transcriptOther;
	const alignStyle = kind === 'rep' ? styles.transcriptRowRight : styles.transcriptRowLeft;

	return (
		<div className={css(styles.transcriptRow, alignStyle)}>
			<div className={css(styles.transcript, sideStyle)} title={time}>
				<div className={css(styles.transcriptHeader)}>
					<span className={css(styles.transcriptEyebrow)}>transcript</span>
					<span className={css(styles.transcriptSpeaker)}>{label}</span>
					<span className={css(styles.transcriptTime)}>{time}</span>
				</div>
				<div className={css(styles.transcriptBody)}>{body}</div>
			</div>
		</div>
	);
};

const MessageText: FC<{ text: string }> = ({ text }) => <AIMarkdownResponse>{text}</AIMarkdownResponse>;

const styles = StyleSheet.create({
	aiWrap: {
		position: 'relative',
		padding: '14px 18px',
	},
	aiHeader: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		marginBottom: 6,
	},
	avatar: {
		width: 20,
		height: 20,
		borderRadius: 6,
		background: 'var(--ink)',
		color: 'var(--bg)',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	aiAuthor: {
		fontSize: 12,
		fontWeight: 500,
		color: 'var(--ink)',
	},
	aiTime: {
		color: 'var(--ink-faint)',
		fontSize: 11,
	},
	aiBody: {
		fontSize: 14,
		color: 'var(--ink)',
		lineHeight: 1.55,
		// Reserve space for the absolute-positioned pin toolbar (top: 8, right:
		// 10, ~28px wide). Without this, long unbroken text on narrow column
		// widths (~280-320px) renders under the toolbar.
		paddingRight: 36,
	},
	toolbar: {
		position: 'absolute',
		top: 8,
		right: 10,
		display: 'flex',
		gap: 2,
		background: 'var(--bg-elev)',
		borderRadius: 8,
		padding: 2,
		boxShadow: '0 0 0 1px var(--border)',
		transition: 'opacity 120ms',
	},
	toolbarVisible: {
		opacity: 1,
	},
	toolbarHidden: {
		opacity: 0,
		pointerEvents: 'none',
	},
	pinBtn: {
		width: 28,
		height: 28,
		padding: 0,
		borderRadius: THEME.common.borderRadius,
		color: 'var(--ink-muted)',
		background: 'transparent',
		border: 'none',
		cursor: 'pointer',
		transition: 'background 120ms, color 120ms',
		':hover': {
			background: 'var(--hover)',
			color: 'var(--ink)',
		},
	},
	userWrap: {
		display: 'flex',
		justifyContent: 'flex-end',
		padding: '8px 0',
	},
	userBubble: {
		position: 'relative',
		maxWidth: '85%',
		padding: '10px 14px',
		background: 'var(--you-bg)',
		borderRadius: 14,
		borderBottomRightRadius: 4,
		fontSize: 13.5,
		color: 'var(--ink)',
	},
	userTime: {
		position: 'absolute',
		bottom: -16,
		right: 2,
		fontSize: 10,
		color: 'var(--ink-faint)',
		fontFamily: 'var(--font-mono)',
		transition: 'opacity 120ms',
	},
	userTimeVisible: {
		opacity: 1,
	},
	userTimeHidden: {
		opacity: 0,
	},
	transcriptRow: {
		display: 'flex',
		padding: '2px 4px',
	},
	transcriptRowLeft: {
		justifyContent: 'flex-start',
	},
	transcriptRowRight: {
		justifyContent: 'flex-end',
	},
	transcript: {
		maxWidth: '85%',
		minWidth: 0,
		padding: '6px 10px',
		borderRadius: 8,
		background: 'var(--bg-elev)',
		fontSize: 12.5,
		color: 'var(--ink)',
		lineHeight: 1.5,
	},
	transcriptLead: {
		borderLeft: '3px solid var(--accent)',
	},
	transcriptRep: {
		borderRight: '3px solid var(--ink-muted)',
	},
	transcriptOther: {
		borderLeft: '3px solid var(--border)',
	},
	transcriptHeader: {
		display: 'flex',
		alignItems: 'baseline',
		gap: 6,
		marginBottom: 2,
	},
	transcriptEyebrow: {
		fontFamily: 'var(--font-mono)',
		fontSize: 10,
		textTransform: 'uppercase',
		letterSpacing: '0.04em',
		color: 'var(--ink-muted)',
	},
	transcriptSpeaker: {
		fontSize: 11,
		fontWeight: 600,
		color: 'var(--ink)',
	},
	transcriptTime: {
		fontSize: 10,
		color: 'var(--ink-faint)',
		fontFamily: 'var(--font-mono)',
		marginLeft: 'auto',
	},
	transcriptBody: {
		fontSize: 12.5,
		color: 'var(--ink)',
		wordBreak: 'break-word',
	},
	paragraph: {
		margin: '0 0 8px 0',
		':last-child': {
			marginBottom: 0,
		},
	},
});
